import { createSelector } from 'reselect';
import { get, isEmpty, isFinite } from 'lodash';

import {
  isSingleOrMultipleQuestionType,
  isMatrixSingleMultipleOrBipolarQuestionType,
} from 'utils/nodeTypes';

import { deriveQuotaMode, QUOTA_MODE } from 'data/quotaModes';

import { getFilteredQuotaStatementsByType } from 'containers/QuestionnaireLogic/utils';

import {
  questionnaireLogicService,
  QuotaConditionTypeEnum,
} from 'utils/adl-services';
import { isQuotaFullReason } from 'components/LogicOutcome/utils';

// Base selectors from QuestionnairePage state
const selectQuestionnaireQuestions = (state) =>
  get(state, 'questionnairePage.surveyDetailsExtended.questions', []);
const selectQuestionnaireLogicQl = (state) =>
  get(state, 'questionnairePage.questionnaireLogic.ql', {});
const selectQuestionnaireQuotaCounters = (state) =>
  get(state, 'questionnairePage.questionnaireQuotaCounters.data', []);
const selectAnswersWithLogicFromOutcomesHide = (state) =>
  get(
    state,
    'questionnairePage.questionnaireLogic.answersWithLogicFromOutcomesHide',
    [],
  );
const selectPendingQuotaMode = (state) =>
  get(
    state,
    'questionnairePage.questionnaireQuotaCounters.pendingQuotaMode',
    {},
  );

const VALID_QUOTA_MODES = new Set(Object.values(QUOTA_MODE));

/**
 * Pending SET_NODE_QUOTA_MODE destination, else the mode derived from persisted state.
 */
const resolvePendingOrDerivedMode = (pendingMode, derivedMode) =>
  VALID_QUOTA_MODES.has(pendingMode) ? pendingMode : derivedMode;

/**
 * Whether a question has assigned quota counts that would actually be lost when
 * switching its quota fill strategy single -> multiple. Used to decide whether
 * that switch needs a confirmation.
 *
 * A counter only matters when its assigned count is non-zero AND diverges from
 * the total collected count: if assigned === total the single-assignment
 * distribution matches the raw totals, so nothing meaningful is lost and the
 * confirmation can be skipped.
 */
export const selectHasAssignedQuotaCountersForQuestion = (
  state,
  questionRefId,
) =>
  selectQuestionnaireQuotaCounters(state).some((counter) => {
    if (counter.questionRefId !== questionRefId) {
      return false;
    }

    const total = counter.resultsCount || 0;
    const assigned = counter.previousRevisionsAssigned || 0;

    return Boolean((total || assigned) && assigned !== total);
  });

/**
 * Minimal per-question quota selector driven by QL targets and server counters.
 * Returns: { [questionRefId]: { items: Array<{ answerRefId, columnRefId?, collected, remaining }>, advancedQuotasPresent: boolean, simpleQuotasPresent: boolean, quotaLimitType?: string, quotaMode: 'none' | 'max' | 'min' } }
 * - Does not read quotas from nodes/answers
 * - Targets come from QL (simple quotas only)
 * - Collected comes from questionnaireQuotaCounters
 * - quotaLimitType comes from the question node (set only for minimum responses)
 * - quotaMode is pending while a SET_NODE_QUOTA_MODE request is in flight,
 *   otherwise derived from simpleQuotasPresent and quotaLimitType
 */
export const makeSelectQuestionQuotasMinimal = createSelector(
  selectQuestionnaireQuestions,
  selectQuestionnaireLogicQl,
  selectQuestionnaireQuotaCounters,
  selectAnswersWithLogicFromOutcomesHide,
  selectPendingQuotaMode,
  generateQuotaMinimalMapFromData,
);

const getQuotaInfoFromStatement = (statement, questionType) => {
  const { outcome, rule } = statement;
  const reason = get(outcome, 'reason', undefined);
  const quota = get(rule, 'quota.value', undefined);
  const condition = get(rule, 'conditions[0]', undefined);

  if (isQuotaFullReason(reason) && isFinite(quota) && condition) {
    if (isSingleOrMultipleQuestionType(questionType)) {
      return {
        answerRefId: condition.value,
        remaining: quota,
        ruleId: rule.id,
      };
    }

    if (isMatrixSingleMultipleOrBipolarQuestionType(questionType)) {
      return {
        answerRefId: condition.value.row,
        columnRefId: condition.value.column,
        remaining: quota,
        ruleId: rule.id,
      };
    }
  }

  return null;
};

/**
 * Optinal helper: build a lookup map for quick access by composite key.
 * Key format: `${answerRefId}::${columnRefId ?? ''}`
 */
export const makeSelectQuestionQuotasMinimalMap = createSelector(
  makeSelectQuestionQuotasMinimal,
  (byQuestion) => {
    const maps = {};
    Object.keys(byQuestion).forEach((questionRefId) => {
      const {
        items,
        advancedQuotasPresent = false,
        simpleQuotasPresent = false,
        quotaLimitType,
        quotaMode = QUOTA_MODE.NONE,
      } = byQuestion[questionRefId] || { items: [] };
      const map = {
        simpleQuotasPresent,
        advancedQuotasPresent,
        quotaLimitType,
        quotaMode,
        items: {},
      };
      items.forEach(
        ({
          answerRefId,
          columnRefId,
          collected,
          remaining,
          revisionCount,
          previousRevisionsAssigned,
          ruleId,
        }) => {
          let key = `${answerRefId}`;
          if (columnRefId) {
            key += `::${columnRefId}`;
          }
          map.items[key] = {
            collected,
            remaining,
            revisionCount,
            previousRevisionsAssigned,
            ruleId,
          };
        },
      );
      maps[questionRefId] = map;
    });
    return maps;
  },
);

function generateQuotaMinimalMapFromData(
  questions = [],
  questionnaireQl = {},
  allCounters = [],
  answersWithLogicFromOutcomesHide = [],
  pendingQuotaMode = {},
) {
  const byQuestion = {};

  function handleQuestion(question) {
    const { refId: questionRefId, type } = question;
    const pendingMode = pendingQuotaMode[questionRefId];

    const items = [];
    if (isEmpty(questionnaireQl)) {
      return;
    }

    let qStatements;
    try {
      qStatements = questionnaireLogicService.findNodeStatementsByNodeId(
        questionnaireQl,
        questionRefId,
      );
    } catch (error) {
      byQuestion[questionRefId] = {
        items: [],
        advancedQuotasPresent: false,
        simpleQuotasPresent: false,
        quotaLimitType: question.quotaLimitType,
        quotaMode: resolvePendingOrDerivedMode(
          pendingMode,
          deriveQuotaMode({
            simpleQuotasPresent: false,
            quotaLimitType: question.quotaLimitType,
          }),
        ),
      };

      if (question.questions) {
        question.questions.forEach(handleQuestion);
      }
      return;
    }

    // Screening quotas are Simple too (normalized on load); only the node's
    // isScreening tells them apart from answer quotas.
    const simpleQuotaStatements = getFilteredQuotaStatementsByType(
      qStatements?.statements,
      QuotaConditionTypeEnum.Simple,
    );
    const advancedQuotaStatements = getFilteredQuotaStatementsByType(
      qStatements?.statements,
      QuotaConditionTypeEnum.Advanced,
    );
    const relevantCounters = allCounters.filter(
      (q) =>
        q.questionRefId === questionRefId &&
        q.quotaType === QuotaConditionTypeEnum.Simple,
    );

    if (isSingleOrMultipleQuestionType(type)) {
      simpleQuotaStatements.forEach((statement) => {
        const quotaInfo = getQuotaInfoFromStatement(statement, type);
        if (!quotaInfo) return;
        if (answersWithLogicFromOutcomesHide.includes(quotaInfo.answerRefId))
          return;

        const counter = relevantCounters.find(
          (q) =>
            q.answerRefId === quotaInfo?.answerRefId &&
            (q.columnRefId === quotaInfo?.columnRefId ||
              (!q.columnRefId && !quotaInfo?.columnRefId)),
        );
        quotaInfo.collected = counter?.resultsCount || 0;
        quotaInfo.revisionCount = counter?.revisionCount || 0;
        quotaInfo.previousRevisionsAssigned =
          counter?.previousRevisionsAssigned;
        items.push(quotaInfo);
      });
    } else if (isMatrixSingleMultipleOrBipolarQuestionType(type)) {
      simpleQuotaStatements.forEach((statement) => {
        const quotaInfo = getQuotaInfoFromStatement(statement, type);
        if (!quotaInfo) return;
        if (
          answersWithLogicFromOutcomesHide.includes(quotaInfo.answerRefId) ||
          answersWithLogicFromOutcomesHide.includes(quotaInfo.columnRefId)
        ) {
          return;
        }

        const counter = relevantCounters.find(
          (q) =>
            q.answerRefId === quotaInfo.answerRefId &&
            q.columnRefId === quotaInfo.columnRefId,
        );
        quotaInfo.collected = counter?.resultsCount || 0;
        quotaInfo.revisionCount = counter?.revisionCount || 0;
        quotaInfo.previousRevisionsAssigned =
          counter?.previousRevisionsAssigned;
        items.push(quotaInfo);
      });
    }

    const advancedQuotasPresent = advancedQuotaStatements.length > 0;
    const simpleQuotasPresent = simpleQuotaStatements.length > 0;

    byQuestion[questionRefId] = {
      items,
      advancedQuotasPresent,
      simpleQuotasPresent,
      quotaLimitType: question.quotaLimitType,
      quotaMode: resolvePendingOrDerivedMode(
        pendingMode,
        deriveQuotaMode({
          simpleQuotasPresent,
          quotaLimitType: question.quotaLimitType,
        }),
      ),
    };

    if (question.questions) {
      question.questions.forEach(handleQuestion);
    }
  }
  questions.forEach(handleQuestion);

  return byQuestion;
}
